/* eslint-disable max-len */
import React, { useMemo, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import pathToRegexp from 'path-to-regexp';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { useSelector, useStore } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { removeUrlParam } from 'lib/helpers';
// Import Code Split Bundles
import {
	HomePage,
	FacilityPage,
	BookingConfirmation,
	PSPPage,
	MovingTruckCLPPage,
	UnitPage,
	LegalConsumerPage,
	LegalGiftCardTermsPage,
	LegalConsumerPrivacyPage,
	LegalConsumerPaymentPage,
	LegalClientLTVPage,
	LegalReviewsPage,
	NewFourOhFourPage,
	UnsubscribePage,
	SizeGuidePage,
	StateLandingPage,
	MovingTruckPage,
	MovingSuppliesPage,
	PortableStoragePage,
	ReviewPage,
	SearchPageWrapper,
	RebateClaimPage,
	RebateStatusPage,
	MoveInConfirmStatusPage,
	MoveInIncentivePage,
	VehicleLengthPage,
	PaidTemplatePage,
	EmailAuthPage,
	OMIPage,
	StyleGuidePage,
} from 'pages/Bundles';

// Import Page Actions
import {
	getBookingPage,
	getHomepage,
	getUnsubscribePage,
	getLegalPage,
	getRebateClaimPage,
	getRebateStatusPage,
	getMoveInIncentivePage,
	getMovingSuppliesPage,
	getPortableStorageCityLandingPage,
	getUnitPage,
	getStateLandingPage,
	getReviewPage,
	getSupplementalReviewData,
	getPSPLandingPage,
	getMovingTruckPage,
	getMovingTruckCityLandingPage,
	getMoveInConfirmStatusPage,
	vehicleLengthPageSuccess,
	getPaidTemplatePage,
} from 'store/pages/slice';

import { getFacilityPage } from 'store/facilities/slice';

import {
	getSearchPage,
	getCityLandingPage,
	getNearMePages,
	getVehicleLandingPage,
	getAmenityLandingPage,
	getNeighborhoodLandingPage,
	getFiveByFiveLandingPage,
	getTenByTenLandingPage,
	getFiveByTenLandingPage,
	getFiveByFifteenLandingPage,
	getTenByFifteenLandingPage,
	getTenByTwentyLandingPage,
	getTenByTwentyFiveLandingPage,
	getTenByThirtyLandingPage,
	getMiniStorageLandingPage,
} from 'store/search/slice';

function parseParamsFromPath(pathname, routePath) {
	if (!routePath) return {};
	const keys = [];
	const re = pathToRegexp(routePath, keys, { end: true });
	const result = re.exec(pathname);
	if (!result) return {};
	return keys.reduce((acc, key, index) => {
		if (result[index + 1] !== undefined) {
			acc[key.name] = result[index + 1];
		}
		return acc;
	}, {});
}

function useHistoryShim() {
	const navigate = useNavigate();
	return useMemo(() => ({
		push: (path, state) => navigate(path, { state }),
		replace: (path, state) => navigate(path, { replace: true, state }),
		goBack: () => navigate(-1),
		go: (n) => navigate(n),
	}), [navigate]);
}

let appHydrated = false;

function useClientDataLoader() {
	const store = useStore();
	const prevPathRef = useRef(null);
	const location = useLocation();

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const currentPath = location.pathname;
		if (prevPathRef.current === currentPath) return;
		const isFirstMount = prevPathRef.current === null;
		prevPathRef.current = currentPath;

		if (isFirstMount && !appHydrated) {
			appHydrated = true;
			return;
		}
		appHydrated = true;

		const queryParams = Object.fromEntries(new URLSearchParams(location.search));

		for (const route of staticRoutes) {
			if (!route.path || !route.loadData || !route.actions) continue;
			const keys = [];
			const re = pathToRegexp(route.path, keys, { end: true });
			const pathWithoutHtml = currentPath.replace(/\.html$/, '');
			const routeResult = re.exec(currentPath) || re.exec(pathWithoutHtml);
			if (routeResult) {
				const params = keys.reduce((acc, key, index) => {
					if (routeResult[index + 1] !== undefined) {
						acc[key.name] = routeResult[index + 1];
					}
					return acc;
				}, {});
				const match = { params, url: currentPath, path: route.path, isExact: true };
				const boundActions = bindActionCreators(route.actions, store.dispatch);
				route.loadData({ actions: boundActions, match, queryParams });
				return;
			}
		}
	}, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}

function RouteElement({ component: Component, pageType, keyword, routePath }) { // eslint-disable-line react/prop-types
	const location = useLocation();
	const history = useHistoryShim();
	const routerParams = useParams();
	const params = routePath ? parseParamsFromPath(location.pathname, routePath) : routerParams;
	const queryParams = Object.fromEntries(new URLSearchParams(location.search));
	const match = { params, url: location.pathname, path: routePath || location.pathname, isExact: true };
	useClientDataLoader();
	return <Component location={location} history={history} match={match} queryParams={queryParams} pageType={pageType} keyword={keyword} />;
}

// Ambiguous single-segment routes that React Router v7 cannot distinguish
// (they all reduce to /:param). We use path-to-regexp to match the original
// regex pattern and render the correct component.
function DynamicSlugRoute() {
	const location = useLocation();
	const history = useHistoryShim();
	const queryParams = Object.fromEntries(new URLSearchParams(location.search));
	const pathname = location.pathname.replace(/\.html$/, '');
	useClientDataLoader();

	for (let i = 0; i < dynamicSlugRoutes.length; i++) {
		const route = dynamicSlugRoutes[i];
		const keys = [];
		const re = pathToRegexp(route.path, keys, { end: true });
		const result = re.exec(pathname) || re.exec(location.pathname);
		if (result) {
			const params = keys.reduce((acc, key, index) => {
				if (result[index + 1] !== undefined) {
					acc[key.name] = result[index + 1];
				}
				return acc;
			}, {});
			const match = { params, url: location.pathname, path: route.path, isExact: true };
			const Component = route.component;
			return <Component location={location} history={history} match={match} queryParams={queryParams} pageType={route.pageType} keyword={route.keyword} />;
		}
	}

	return <NewFourOhFourPage location={location} />;
}

// Routes that all match /:singleSegment pattern and need path-to-regexp
// disambiguation on the client. Order matters — more specific patterns first.
const dynamicSlugRoutes = [
	// CLP: Austin-TX-self-storage (must be before state landing)
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage',
		component: SearchPageWrapper,
		pageType: PAGE_TYPES.CITY_LANDING,
	},
	// PSP: rv-storage, car-storage, etc.
	{
		path: '/:keyword(rv|car|climate-controlled|boat|portable|motorcycle|business|wine|atv|bicycle|dirt-bike|golf-cart|college|truck|jet-ski|kayak|military|drive-up|24-hour|vehicle)-storage',
		component: PSPPage,
		pageType: undefined,
	},
	// State landing: Texas-storage, California-storage, etc.
	{
		path: '/:state([A-Za-z-]+)-storage',
		component: StateLandingPage,
		pageType: undefined,
	},
];

// Routes that match /:clpSlug/:subSegment and need disambiguation
const dynamicClpSubRoutes = [
	// Unit page: Austin-TX-self-storage/facility-name-12345/reserve/456
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/*-:facilityId(\\d{3,})(/reserve)?/:unitId(\\d+)',
		component: UnitPage,
		pageType: 'unit',
	},
	// Vehicle landing: Austin-TX-self-storage/car-storage
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:vehicle(car|boat|rv)-storage',
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.VEHICLE_LANDING,
	},
	// Amenity landing: Austin-TX-self-storage/climate-controlled
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:amenity(climate-controlled|24-hour-access|drive-up-storage-units|wine-storage|storage-lockers|storage-with-electricity|storage-with-free-truck-rental)',
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.AMENITY_LANDING,
	},
	// Size landing pages
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x5-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_5x5_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x10-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_10x10_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x10-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_5x10_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x15-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_5x15_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x15-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_10x15_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x20-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_10x20_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x25-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_10x25_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x30-storage-units',
		component: SearchPageWrapper,
		pageType: 'city_10x30_landing_page',
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/mini-storage',
		component: SearchPageWrapper,
		pageType: 'city_mini_storage_landing_page',
	},
	// Facility page: Austin-TX-self-storage/facility-name-12345
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/*-:facilityId(\\d{3,})',
		component: FacilityPage,
		pageType: undefined,
	},
	// Neighborhood landing: Austin-TX-self-storage/downtown
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:neighborhood([A-Za-z-0-9]+)',
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING,
	},
];

function DynamicClpSubRoute() {
	const location = useLocation();
	const history = useHistoryShim();
	const queryParams = Object.fromEntries(new URLSearchParams(location.search));
	const pathname = location.pathname.replace(/\.html$/, '');
	useClientDataLoader();

	for (let i = 0; i < dynamicClpSubRoutes.length; i++) {
		const route = dynamicClpSubRoutes[i];
		const keys = [];
		const re = pathToRegexp(route.path, keys, { end: true });
		const result = re.exec(pathname) || re.exec(location.pathname);
		if (result) {
			const params = keys.reduce((acc, key, index) => {
				if (result[index + 1] !== undefined) {
					acc[key.name] = result[index + 1];
				}
				return acc;
			}, {});
			const match = { params, url: location.pathname, path: route.path, isExact: true };
			const Component = route.component;
			return <Component location={location} history={history} match={match} queryParams={queryParams} pageType={route.pageType} keyword={route.keyword} />;
		}
	}

	return <NewFourOhFourPage location={location} />;
}

export const staticRoutes = [
	{
		path: '/',
		exact: true,
		component: HomePage,
		actions: { getHomepage },
		loadData: (props) => props.actions.getHomepage(),
	},
	{
		path: '/unsubscribe(.html)?',
		clientPath: '/unsubscribe',
		exact: true,
		actions: { getUnsubscribePage },
		component: UnsubscribePage,
		loadData: (props) => props.actions.getUnsubscribePage(),
	},
	{
		path: '/reservation/(cancellation|confirmation)/:confirmationCode([A-Za-z0-9]+)(.html)?',
		clientPath: '/reservation/:type/:confirmationCode',
		exact: true,
		actions: { getBookingPage },
		component: (props) => {
			const statusCode = useSelector((state) => state.pageMeta.statusCode);
			if ([401, 403].includes(statusCode)) {
				const { email, ...restQueryParams } = props.queryParams || {};
				if (email) {
					removeUrlParam('email');
				}
				return <EmailAuthPage {...props} queryParams={restQueryParams} />;
			}
			return <BookingConfirmation {...props} />;
		},
		loadData: (props) =>
			props.actions.getBookingPage(
				props.match.params.confirmationCode,
				props.queryParams
			),
	},
	{
		path: '/reservation/online-move-in/:confirmationCode([A-Za-z0-9]+)',
		clientPath: '/reservation/online-move-in/:confirmationCode',
		actions: { getBookingPage },
		exact: true,
		component: OMIPage,
		loadData: (props) =>
			props.actions.getBookingPage(
				props.match.params.confirmationCode,
				props.queryParams
			),
	},
	{
		path: '/legal/consumer(.html)?',
		clientPath: '/legal/consumer',
		exact: true,
		actions: { getLegalPage },
		component: LegalConsumerPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/gift-card-terms(.html)?',
		clientPath: '/legal/gift-card-terms',
		exact: true,
		actions: { getLegalPage },
		component: LegalGiftCardTermsPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/privacy(.html)?',
		clientPath: '/legal/privacy',
		exact: true,
		actions: { getLegalPage },
		component: LegalConsumerPrivacyPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/:pageType(client|ltv)(.html)?',
		clientPath: '/legal/:pageType',
		exact: true,
		actions: { getLegalPage },
		component: LegalClientLTVPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/:pageType(client|ltv)/:version([0-9]+)(.html)?',
		clientPath: '/legal/:pageType/:version',
		exact: true,
		actions: { getLegalPage },
		component: LegalClientLTVPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/consumer/payment(.html)?',
		clientPath: '/legal/consumer/payment',
		exact: true,
		actions: { getLegalPage },
		component: LegalConsumerPaymentPage,
		loadData: (props) => props.actions.getLegalPage('legal'),
	},
	{
		path: '/legal/reviewstoc(.html)?',
		clientPath: '/legal/reviewstoc',
		exact: true,
		actions: { getLegalPage },
		component: LegalReviewsPage,
		loadData: (props) => props.actions.getLegalPage('reviewstoc'),
	},
	{
		path: '/rebate(.html)?',
		clientPath: '/rebate',
		exact: true,
		component: RebateClaimPage,
		actions: { getRebateClaimPage },
		loadData: (props) => props.actions.getRebateClaimPage(),
	},

	{
		path: '/rebate/status(.html)?',
		clientPath: '/rebate/status',
		exact: true,
		component: RebateStatusPage,
		actions: { getRebateStatusPage },
		loadData: (props) => props.actions.getRebateStatusPage(),
	},
	{
		path: '/rebate/move-in/:confirmationCode([A-Za-z0-9]+)/:offerId([A-Za-z0-9]+)(.html)?',
		clientPath: '/rebate/move-in/:confirmationCode/:offerId',
		exact: true,
		component: MoveInIncentivePage,
		actions: { getMoveInIncentivePage },
		loadData: (props) =>
			props.actions.getMoveInIncentivePage(
				props.match.params.confirmationCode,
				props.match.params.offerId
			),
	},
	{
		path: '/moving-supplies(.html)?',
		clientPath: '/moving-supplies',
		exact: true,
		actions: { getMovingSuppliesPage },
		component: MovingSuppliesPage,
		loadData: (props) => props.actions.getMovingSuppliesPage(),
	},
	{
		path: '/storage-unit-size-guide(.html)?',
		clientPath: '/storage-unit-size-guide',
		exact: true,
		actions: { getPSPLandingPage },
		component: SizeGuidePage,
		loadData: (props) => props.actions.getPSPLandingPage('size'),
	},

	{
		path: '/portable-storage/:city([A-Za-z0-9-\\.]+)-:state([A-Za-z]{2})(.html)?',
		clientPath: '/portable-storage/:locationSlug',
		exact: true,
		actions: { getPortableStorageCityLandingPage },
		component: PortableStoragePage,
		loadData: (props) =>
			props.actions.getPortableStorageCityLandingPage(
				props.match.params.city,
				props.match.params.state
			),
	},
	{
		path: '/facility/:facilityId(\\d{3,})/unit/:unitId(\\d{3,})(.html)?',
		clientPath: '/facility/:facilityId/unit/:unitId',
		exact: true,
		actions: { getUnitPage },
		component: UnitPage,
		loadData: (props) =>
			props.actions.getUnitPage(
				props.match.params.unitId,
				props.match.params.facilityId
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/*-:facilityId(\\d{3,})(/reserve)?/:unitId(\\d+)(.html)?',
		clientPath: '/:clpSlug/:facilitySlug/:unitSlug',
		exact: true,
		actions: { getUnitPage },
		component: UnitPage,
		pageType: 'unit',
		loadData: (props) =>
			props.actions.getUnitPage(
				props.match.params.unitId,
				props.match.params.facilityId
			),
	},
	{
		path: '/search(.html)?',
		clientPath: '/search',
		exact: true,
		actions: { getSearchPage },
		component: SearchPageWrapper,
		pageType: PAGE_TYPES.SEARCH,
		key: 'search',
		loadData: (props) => props.actions.getSearchPage(props.queryParams),
	},
	{
		path: '/near-me/:keyword(rv-storage|car-storage|climate-controlled-storage|boat-storage|portable-storage|motorcycle-storage|business-storage|wine-storage|atv-storage|bicycle-storage|dirt-bike-storage|golf-cart-storage|college-storage|truck-storage|jet-ski-storage|kayak-storage|military-storage|drive-up-storage|24-hour-storage|vehicle-storage|mini-storage|5x5-storage|5x10-storage|5x15-storage|10x10-storage|10x15-storage|10x20-storage|10x25-storage|10x30-storage|locker-storage|storage-with-electricity|storage-deals|storage-with-free-truck-rental)(.html)?',
		clientPath: '/near-me/:keyword',
		exact: true,
		actions: { getNearMePages },
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING,
		key: 'near-me',
		loadData: (props) =>
			props.actions.getNearMePages(props.match.params, props.queryParams),
	},
	{
		path: '/review(.html)?',
		clientPath: '/review',
		exact: true,
		actions: { getReviewPage, getSupplementalReviewData },
		component: ReviewPage,
		loadData: (props) =>
			Promise.all([
				props.actions.getReviewPage(),
				props.actions.getSupplementalReviewData(props.queryParams),
			]),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage(.html)?',
		clientPath: '/:clpSlug',
		exact: true,
		actions: { getCityLandingPage },
		component: SearchPageWrapper,
		pageType: PAGE_TYPES.CITY_LANDING,
		key: 'search',
		loadData: (props) =>
			props.actions.getCityLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:vehicle(car|boat|rv)-storage(.html)?',
		clientPath: '/:clpSlug/:vehicleSlug',
		exact: true,
		actions: { getVehicleLandingPage },
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.VEHICLE_LANDING,
		key: 'search',
		loadData: (props) =>
			props.actions.getVehicleLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:amenity(climate-controlled|24-hour-access|drive-up-storage-units|wine-storage|storage-lockers|storage-with-electricity|storage-with-free-truck-rental)(.html)?',
		clientPath: '/:clpSlug/:amenitySlug',
		exact: true,
		actions: { getAmenityLandingPage },
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.AMENITY_LANDING,
		key: 'search',
		loadData: (props) =>
			props.actions.getAmenityLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x5-storage-units(.html)?',
		clientPath: '/:clpSlug/5x5-storage-units',
		exact: true,
		actions: { getFiveByFiveLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_5x5_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getFiveByFiveLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x10-storage-units(.html)?',
		clientPath: '/:clpSlug/10x10-storage-units',
		exact: true,
		actions: { getTenByTenLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_10x10_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getTenByTenLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x10-storage-units(.html)?',
		clientPath: '/:clpSlug/5x10-storage-units',
		exact: true,
		actions: { getFiveByTenLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_5x10_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getFiveByTenLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/5x15-storage-units(.html)?',
		clientPath: '/:clpSlug/5x15-storage-units',
		exact: true,
		actions: { getFiveByFifteenLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_5x15_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getFiveByFifteenLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x15-storage-units(.html)?',
		clientPath: '/:clpSlug/10x15-storage-units',
		exact: true,
		actions: { getTenByFifteenLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_10x15_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getTenByFifteenLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x20-storage-units(.html)?',
		clientPath: '/:clpSlug/10x20-storage-units',
		exact: true,
		actions: { getTenByTwentyLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_10x20_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getTenByTwentyLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x25-storage-units(.html)?',
		clientPath: '/:clpSlug/10x25-storage-units',
		exact: true,
		actions: { getTenByTwentyFiveLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_10x25_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getTenByTwentyFiveLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/10x30-storage-units(.html)?',
		clientPath: '/:clpSlug/10x30-storage-units',
		exact: true,
		actions: { getTenByThirtyLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_10x30_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getTenByThirtyLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/mini-storage(.html)?',
		clientPath: '/:clpSlug/mini-storage',
		exact: true,
		actions: { getMiniStorageLandingPage },
		component: SearchPageWrapper,
		pageType: 'city_mini_storage_landing_page',
		key: 'search',
		loadData: (props) =>
			props.actions.getMiniStorageLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/*-:facilityId(\\d{3,})(.html)?',
		clientPath: '/:clpSlug/:facilitySlug',
		exact: true,
		actions: { getFacilityPage, getHomepage },
		component: FacilityPage,
		loadData: (props) =>
			Promise.all([
				props.actions.getFacilityPage(
					props.match.params.facilityId,
					props.match.params.city,
					props.match.params.state
				),
				props.actions.getHomepage(),
			]),
	},
	{
		path: '/(facility|facilities)/:facilityId(\\d{3,})(.html)?',
		clientPath: '/facility/:facilityId',
		clientPaths: ['/facility/:facilityId', '/facilities/:facilityId'],
		exact: true,
		actions: { getFacilityPage, getHomepage },
		component: FacilityPage,
		loadData: (props) =>
			Promise.all([
				props.actions.getFacilityPage(props.match.params.facilityId),
				props.actions.getHomepage(),
			]),
	},
	{
		path: '/:city-:state([A-Za-z]{2})-self-storage/:neighborhood([A-Za-z-0-9]+)(.html)?',
		clientPath: '/:clpSlug/:neighborhoodSlug',
		exact: true,
		actions: { getNeighborhoodLandingPage },
		component: SearchPageWrapper,
		pageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING,
		key: 'search',
		loadData: (props) =>
			props.actions.getNeighborhoodLandingPage(
				props.match.params,
				props.queryParams
			),
	},
	// PSPs - Dynamic route for all storage type pages
	{
		path: '/:keyword(rv|car|climate-controlled|boat|portable|motorcycle|business|wine|atv|bicycle|dirt-bike|golf-cart|college|truck|jet-ski|kayak|military|drive-up|24-hour|vehicle)-storage(.html)?',
		clientPath: '/:pspSlug',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		loadData: async ({ actions, match }) => {
			const keyword = match.params.keyword || '';
			return actions.getPSPLandingPage(keyword);
		},
	},
	{
		path: '/storage-unit-size-guide/:keyword(mini-storage)(.html)?',
		clientPath: '/storage-unit-size-guide/mini-storage',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		loadData: async ({ actions, match }) => {
			const keyword = match.params.keyword || '';
			return actions.getPSPLandingPage(keyword);
		},
		keyword: 'mini storage',
	},
	{
		path: '/storage-unit-size-guide/:keyword(storage-lockers)(.html)?',
		clientPath: '/storage-unit-size-guide/storage-lockers',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		loadData: async ({ actions, match }) => {
			const keyword = match.params.keyword || '';
			return actions.getPSPLandingPage(keyword);
		},
	},
	{
		path: '/storage-unit-size-guide/:keyword(5x5|5x10|5x15|10x10|10x15|10x20|10x25|10x30)-storage-unit(.html)?',
		clientPath: '/storage-unit-size-guide/:sizeSlug',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		loadData: async ({ actions, match }) => {
			const keyword = match.params.keyword || '';
			return actions.getPSPLandingPage(keyword);
		},
	},
	{
		path: '/storage-101(.html)?',
		clientPath: '/storage-101',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		keyword: 'self',
		loadData: ({ actions }) => {
			const keyword = 'self';
			return actions.getPSPLandingPage(keyword);
		},
	},
	{
		path: '/storage-with-electricity(.html)?',
		clientPath: '/storage-with-electricity',
		exact: true,
		component: PSPPage,
		actions: { getPSPLandingPage },
		keyword: 'electricity',
		loadData: ({ actions }) => {
			const keyword = 'electricity';
			return actions.getPSPLandingPage(keyword);
		},
	},
	{
		path: '/:state([A-Za-z-]+)-storage(.html)?',
		clientPath: '/:stateSlug',
		exact: true,
		component: StateLandingPage,
		actions: { getStateLandingPage },
		loadData: ({ actions, match }) =>
			actions.getStateLandingPage({ state: match.params.state }),
	},
	{
		path: '/moving-truck-rental(.html)?',
		clientPath: '/moving-truck-rental',
		exact: true,
		actions: { getMovingTruckPage },
		component: MovingTruckPage,
		loadData: (props) =>
			props.actions.getMovingTruckPage(props.queryParams),
	},
	{
		path: '/moving-truck-rental/:city([A-Za-z0-9-\\.]+)-:state([A-Za-z]{2})(.html)?',
		clientPath: '/moving-truck-rental/:locationSlug',
		exact: true,
		actions: { getMovingTruckCityLandingPage },
		component: MovingTruckCLPPage,
		loadData: (props) =>
			props.actions.getMovingTruckCityLandingPage(props.match.params),
	},
	{
		path: '/reservation/confirm-:moveInStatus(.*move-in)/:confirmationCode([A-Za-z0-9]+)(.html)?',
		clientPath: '/reservation/:moveInStatus/:confirmationCode',
		exact: true,
		component: MoveInConfirmStatusPage,
		actions: { getMoveInConfirmStatusPage },
		loadData: (props) =>
			props.actions.getMoveInConfirmStatusPage(
				props.match.params.confirmationCode,
				props.match.params.moveInStatus
			),
	},
	{
		path: '/tools/vehicle-length(.html)?',
		clientPath: '/tools/vehicle-length',
		exact: true,
		component: VehicleLengthPage,
		actions: { vehicleLengthPageSuccess },
		loadData: (props) => props.actions.vehicleLengthPageSuccess(),
	},
	{
		path: '/paid/:paidPageUri?', // requires camelcase syntax to fire action & handle /paid/ or /paid/some-route-here dynamically
		clientPath: '/paid/*',
		component: PaidTemplatePage,
		actions: { getPaidTemplatePage },
		loadData: (props) =>
			props.actions.getPaidTemplatePage(
				props.queryParams,
				props.match.url
			),
	},
	{
		path: '/style-guide(.html)?',
		clientPath: '/style-guide',
		exact: true,
		component: StyleGuidePage,
		actions: { getHomepage },
		loadData: (props) => props.actions.getHomepage(),
	},
	{
		component: NewFourOhFourPage,
	},
];

// clientPaths that should be handled by dynamic dispatchers instead of direct Route registration
const DYNAMIC_SLUG_CLIENT_PATHS = ['/:clpSlug', '/:pspSlug', '/:stateSlug'];
const DYNAMIC_CLP_SUB_CLIENT_PATHS = [
	'/:clpSlug/:vehicleSlug', '/:clpSlug/:amenitySlug', '/:clpSlug/:facilitySlug',
	'/:clpSlug/:neighborhoodSlug', '/:clpSlug/:facilitySlug/:unitSlug',
	'/:clpSlug/5x5-storage-units', '/:clpSlug/10x10-storage-units',
	'/:clpSlug/5x10-storage-units', '/:clpSlug/5x15-storage-units',
	'/:clpSlug/10x15-storage-units', '/:clpSlug/10x20-storage-units',
	'/:clpSlug/10x25-storage-units', '/:clpSlug/10x30-storage-units',
	'/:clpSlug/mini-storage',
];

function isDynamicRoute(clientPath) {
	return DYNAMIC_SLUG_CLIENT_PATHS.includes(clientPath) ||
		DYNAMIC_CLP_SUB_CLIENT_PATHS.includes(clientPath);
}

export default function AppRoutes() {
	return (
		<Routes>
			{staticRoutes.flatMap((route, i) => {
				if (!route.path && route.component) {
					return [<Route key="not-found" path="*" element={<RouteElement component={route.component} />} />];
				}
				const paths = route.clientPaths || [route.clientPath || route.path];
				// Skip routes handled by dynamic dispatchers
				if (paths.every((p) => isDynamicRoute(p))) return [];
				const element = <RouteElement component={route.component} pageType={route.pageType} keyword={route.keyword} routePath={route.path} />;
				const hasHtmlInOriginalPath = route.path && route.path.includes('(.html)?');
				return paths.filter((p) => !isDynamicRoute(p)).flatMap((clientPath, j) => {
					const key = route.key || clientPath || `${i}-${j}`;
					if (hasHtmlInOriginalPath) {
						return [
							<Route key={key} path={clientPath} element={element} />,
							<Route key={`${key}-html`} path={`${clientPath}.html`} element={element} />,
						];
					}
					return [<Route key={key} path={clientPath} element={element} />];
				});
			})}
			{/* CLP sub-routes (two+ segments starting with city-state-self-storage) */}
			<Route key="dynamic-clp-sub" path="/:clpSlug/:subSlug" element={<DynamicClpSubRoute />} />
			<Route key="dynamic-clp-sub-html" path="/:clpSlug/:subSlug.html" element={<DynamicClpSubRoute />} />
			<Route key="dynamic-clp-sub-deep" path="/:clpSlug/:subSlug/:deepSlug" element={<DynamicClpSubRoute />} />
			<Route key="dynamic-clp-sub-deep-html" path="/:clpSlug/:subSlug/:deepSlug.html" element={<DynamicClpSubRoute />} />
			<Route key="dynamic-clp-sub-reserve" path="/:clpSlug/:subSlug/reserve/:unitSlug" element={<DynamicClpSubRoute />} />
			<Route key="dynamic-clp-sub-deep-reserve" path="/:clpSlug/:subSlug/:deepSlug/:unitSlug" element={<DynamicClpSubRoute />} />
			{/* Single-segment dynamic routes: CLP, PSP, state landing */}
			<Route key="dynamic-slug" path="/:slug" element={<DynamicSlugRoute />} />
			<Route key="dynamic-slug-html" path="/:slug.html" element={<DynamicSlugRoute />} />
		</Routes>
	);
}
